import React, { useEffect, useRef, useState } from 'react'
import './TacticalMap.scss'

import mapboxgl, { Map } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import { useDispatch } from 'react-redux'

import {
    createAreaLayer,
    createBlueDronesLayer,
    createBlueGcsLayer,
    createDetectionsLayer,
    handleBlueDroneOnClick,
    handleDetectionOnClick,
    stopUpdates,
    updateEntities,
} from '../../../services/mapLayers.service'
import { alliance, entity_types } from '../../../config/constant'
import { droneUpdateSpeed } from '../../../config/config'
import allActions from '../../../redux/actions'
import { getMapboxAccessToken } from '../../../services/usecase.service'
import { Area, AreaInfo } from 'src/redux/reducers/types'
import { Asset } from 'src/redux/reducers/assets'

interface TacticalMapProps {
    selectedFromList: any
    detectionSelectedFromList: any
    useCaseData: any
    activeSessionId: number
}

function TacticalMap(props: TacticalMapProps) {
    const usecaseData = props.useCaseData
    const activeSessionId = props.activeSessionId
    let selectedFromList = props.selectedFromList
    let detectionSelectedFromList = props.detectionSelectedFromList
    const mapContainer = useRef<HTMLDivElement>(null)

    // @ts-ignore 2025-05-23 ST - Seems to work despite the TS error
    const map: React.MutableRefObject<Map> = useRef(null)
    const dispatch = useDispatch()
    const [mapLoaded, setMapLoaded] = useState(false)
    const [firstLabelLayerId, setFirstLabelLayerId] = useState('')
    const [mapLayerIds, setMapLayerIds] = useState<AreaInfo[]>([])
    const [initialUsecase, setInitialUsecase] = useState(null)

    useEffect(() => {
        if (mapLoaded) return

        setMapboxAccessToken().then(() => {
            map.current = new mapboxgl.Map({
                container: mapContainer.current!,
                style: 'mapbox://styles/mapbox/satellite-streets-v11',
                center: [0, 0],
                zoom: 1,
            })
            const nav = new mapboxgl.NavigationControl()
            map.current.addControl(nav, 'bottom-right')
            map.current.dragRotate.disable()
            map.current.touchZoomRotate.disableRotation()
            map.current.on('load', () => {
                setMapLoaded(true)
                setFirstLabelLayerId(map.current.getStyle().layers!.find((layer: any) => layer.type === 'symbol')!.id)
            })
        })
    }, [mapLoaded])

    useEffect(() => {
        if (mapLoaded && usecaseData && firstLabelLayerId) {
            // Load area layers from usecase
            const areas_information: AreaInfo[] = []
            usecaseData.attributes_json.areas.forEach((area: Area) => {
                if (area?.geojson?.features[0]?.geometry?.type === 'Point' && area?.type === 'Neutral') {
                    // Center Point
                    map.current.flyTo({
                        center: area.geojson.features[0].geometry.coordinates,
                        zoom: 6.8,
                        duration: 0,
                    })
                    // Radar Area Layer. TODO: Refactor once radar data is available in the system
                    /*
                    const radarCenter = area.geojson.features[0].geometry.coordinates
                    createRadarLayer(map.current, radarCenter, firstLabelLayerId);
                    areas_information.push({name: 'Radar', id: 'radar'});
					*/

                    // Load Entities from usecase
                    const blue_drones_information = usecaseData.attributes_json.entities.filter((entity: Asset) => {
                        return entity?.type === entity_types.drone && entity?.alliance === alliance.friendly
                    })
                    const blue_assets_information = usecaseData.attributes_json.entities.filter((entity: Asset) => {
                        return entity?.type === entity_types.gcs && entity?.alliance === alliance.friendly
                    })
                    // Map Entities layers creation
                    createBlueDronesLayer(map.current, blue_drones_information, dispatch)
                    createBlueGcsLayer(map.current, blue_assets_information, firstLabelLayerId)
                    // Detection layer creation
                    createDetectionsLayer(map.current, activeSessionId, null, dispatch)
                } else {
                    // Creation of area layers
                    let area_info = createAreaLayer(map.current, area, firstLabelLayerId)
                    areas_information.push(area_info)
                }
            })

            // Set area layers state for visibility controls
            setMapLayerIds(areas_information)
            // Set usecase data state for interaction
            setInitialUsecase(usecaseData)
        }
    }, [mapLoaded, usecaseData, firstLabelLayerId])

    useEffect(() => {
        let interval: any
        if (mapLoaded && initialUsecase && activeSessionId) {
            // Start Fetching for Entities Updates
            interval = setInterval(
                () => {
                    updateEntities(map.current, activeSessionId, dispatch)
                },
                droneUpdateSpeed as unknown as number,
            )
        }
        return () => {
            clearInterval(interval)
            stopUpdates()
            dispatch(allActions.assetActions.resetSelectedAsset())
            dispatch(allActions.assetActions.resetSelectedDetection())
            dispatch(allActions.assetActions.clearDetectionList())
        }
    }, [mapLoaded, initialUsecase, activeSessionId])

    useEffect(() => {
        if (mapLoaded && initialUsecase && selectedFromList) {
            handleBlueDroneOnClick(map.current, selectedFromList, dispatch)
        }
    }, [selectedFromList, mapLoaded, initialUsecase])

    useEffect(() => {
        if (mapLoaded && initialUsecase && detectionSelectedFromList) {
            handleDetectionOnClick(map.current, detectionSelectedFromList, dispatch)
        }
    }, [detectionSelectedFromList, mapLoaded, initialUsecase])

    function renderLayerCheckboxes() {
        return (
            <>
                {mapLayerIds.map((mapLayer, index) => (
                    <div key={index}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked
                                        value={mapLayer.id}
                                        onChange={handleLayerVisibilityChange}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                }
                                label={<Typography variant="subtitle2">{mapLayer.name}</Typography>}
                            />
                        </FormGroup>
                    </div>
                ))}
            </>
        )
    }

    async function setMapboxAccessToken() {
        if (mapboxgl.accessToken == undefined) {
            const result = await getMapboxAccessToken()

            mapboxgl.accessToken = result?.data?.token
        }
    }

    const handleLayerVisibilityChange = (event: React.ChangeEvent<any>) => {
        if (!event.target.checked) {
            map.current.setLayoutProperty('deck_layer_area_' + event.target.value, 'visibility', 'none')
        } else {
            map.current.setLayoutProperty('deck_layer_area_' + event.target.value, 'visibility', 'visible')
        }
    }

    const showLayerCheckboxes = false

    return (
        <div className="TacticalMap" data-test-id="tactical-map-container">
            {showLayerCheckboxes && <div className="LayerTogglesContainer">{renderLayerCheckboxes()}</div>}
            <div ref={mapContainer} className="MapContainer" />
        </div>
    )
}

export default TacticalMap
